import { RECEIPT_CATEGORY_LABEL_KEY } from "@nmible-ltd/claims";
import { Calendar, Store } from "lucide-react";
import { useTranslation } from "react-i18next";

import { InfoItem } from "@/components/info-item";
import { ImageModal } from "@/features/payments/components/image-modal";
import { formatCurrency } from "@/lib/currency-formatter";
import { formatISODate } from "@/lib/date-formatter";

import type { ReceiptDetails } from "../types";

interface PanelReceiptProps {
  details: ReceiptDetails;
  currency: string;
  total?: string;
}

const LineItem = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between gap-3 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-mono">{value}</span>
  </div>
);

export const PanelReceipt = ({
  details,
  currency,
  total,
}: PanelReceiptProps) => {
  const { t } = useTranslation();
  const hasAdjustments = Boolean(
    details.tax || details.discount || details.additionalFees
  );

  return (
    <div className="space-y-2 grid grid-cols-1 md:grid-cols-3 gap-4">
      <ImageModal
        src={details.imageUrl}
        alt={details.merchant ?? t("merchant")}
        thumbnailClassName="rounded-md border border-border"
      />
      <div className="md:col-span-2">
        <div className="grid grid-cols-1 md:grid-cols-2">
          {details.merchant && (
            <InfoItem
              icon={<Store size={18} />}
              label={t("merchant")}
              value={details.merchant}
            />
          )}
          {details.receiptDate && (
            <InfoItem
              icon={<Calendar size={18} />}
              label={t("date")}
              value={formatISODate(details.receiptDate, { format: "date" })}
            />
          )}
        </div>

        {details.items.length > 0 && (
          <>
            <p className="my-1.5 px-3 text-sm font-semibold">{t("items")}</p>
            <ul className="divide-y divide-border px-3">
              {details.items.map((item, index) => {
                const categoryLabel = t(
                  RECEIPT_CATEGORY_LABEL_KEY[item.category] ?? item.category
                );
                const title = item.description?.trim();
                // The receipt flow only generates an item title that adds detail
                // beyond the category; when it matches the category we show the
                // category alone to avoid a duplicated line.
                const showTitle =
                  Boolean(title) &&
                  title.toLowerCase() !== categoryLabel.toLowerCase();

                return (
                  <li
                    key={`${item.description}-${index}`}
                    className="flex justify-between gap-3 py-2"
                    aria-label={item.description}
                  >
                    <div className="min-w-0 flex-1">
                      {showTitle ? (
                        <>
                          <p className="truncate font-medium">{title}</p>
                          <p className="text-sm text-muted-foreground">
                            {categoryLabel}
                          </p>
                        </>
                      ) : (
                        <p className="truncate font-medium">{categoryLabel}</p>
                      )}
                    </div>
                    <span className="font-mono font-medium shrink-0">
                      {formatCurrency(item.amount, currency)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        {hasAdjustments && (
          <div className="space-y-1 pt-2 mt-1 border-t border-border px-3">
            {details.tax && (
              <LineItem
                label={t("tax")}
                value={formatCurrency(details.tax, currency)}
              />
            )}
            {details.discount && (
              <LineItem
                label={t("discount")}
                value={`-${formatCurrency(details.discount, currency)}`}
              />
            )}
            {details.additionalFees && (
              <LineItem
                label={t("additional_fees")}
                value={formatCurrency(details.additionalFees, currency)}
              />
            )}
          </div>
        )}

        {total && (
          <div className="flex justify-between gap-3 pt-3 mt-1 border-t border-border px-3 text-base font-semibold">
            <span>{t("total")}</span>
            <span className="font-mono">{formatCurrency(total, currency)}</span>
          </div>
        )}
      </div>
    </div>
  );
};
